import { useGetInvoiceStatusesConfig } from "@portal/Components/Portal/PotalTabs/Invoice/useInvoiceStatusesConfig";
import { portalTabQueryKeys } from "@portal/Components/Portal/PotalTabs/types";
import {
  InvoiceSearchParams,
  getInvoiceList,
  getInvoicesDetails,
  getLineItem,
  invoiceSearch,
} from "@portal/api/invoice-list";
import { InvoiceListFilters } from "@portal/store/Filters/Invoices/type";
import { tableAtom } from "@portal/store/table/table";
import { RequestConfig } from "@portal/types/common";
import { useMutation, useQuery } from "@tanstack/react-query";
import produce from "immer";
import { useSetAtom } from "jotai";

function useInvoiceListQuery(params: RequestConfig<InvoiceListFilters>) {
  const setTableValue = useSetAtom(tableAtom);
  return useQuery({
    queryKey: [portalTabQueryKeys.INVOICES, params],
    queryFn: () => getInvoiceList(params),
    retry: false,
    async onSuccess(data) {
      setTableValue((value) => {
        return produce(value, (draft) => {
          draft.bulk_records = data.total_records;
          return draft;
        });
      });
    },
    onError(err) {},
  });
}

function useGetDefaultInvoices(openInvoicesOnly?: boolean) {
  const invoiceStatusesConfig = useGetInvoiceStatusesConfig();
  return useQuery({
    queryKey: ["invoice-list-default"],
    queryFn: () =>
      getInvoiceList({
        page: 1,
        size: 20,
        filters: {
          statuses: openInvoicesOnly ? invoiceStatusesConfig.OPEN : undefined,
        },
      }),
    retry: false,
    onError(err) {},
  });
}
function useGetInvoicesById(invoiceId?: string[], openInvoicesOnly?: boolean) {
  const invoiceStatusesConfig = useGetInvoiceStatusesConfig();
  return useQuery({
    queryKey: ["invoice-by-ids"],
    queryFn: () =>
      getInvoiceList({
        page: 1,
        size: 1,
        filters: {
          invoice_ids: invoiceId,
          statuses: openInvoicesOnly ? invoiceStatusesConfig.OPEN : undefined,
        },
      }),
    retry: false,
    enabled: Boolean(invoiceId?.length),
    onError(err) {},
  });
}

function useGetLineItemsQuery(params: string[]) {
  return useQuery({
    queryKey: ["line-items", params],
    queryFn: () => getLineItem(params),
    retry: false,
    enabled: params ? Boolean(params.length) : false,
  });
}
function useGetInvoiceDetailsQuery(id?: string) {
  return useQuery({
    queryKey: ["invoice-details", id],
    queryFn: () => getInvoicesDetails(id),
    retry: false,
    enabled: Boolean(id?.length),
  });
}

function useInvoiceSearchQuery() {
  return useMutation({
    mutationFn: (params: InvoiceSearchParams) => invoiceSearch(params),
    mutationKey: ["invoice-search"],

    onError(err) {
      throw new Error(`${err}`);
    },
  });
}

export {
  useGetDefaultInvoices,
  useGetInvoiceDetailsQuery,
  useGetInvoicesById,
  useGetLineItemsQuery,
  useInvoiceListQuery,
  useInvoiceSearchQuery,
};
